import { Component, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DeepPartial } from '@shared/directives/types';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environments/environment.qc';
import { ApolloQuotationService } from '@core/graphql/apolloQuotation.service';
import { CustomerPoco, CustomerVehiclePoco, PaymentPoco, QuotationPoco, ResponseDTO } from '@data/DTO/DTO';

@Component({
    selector: 'app-quotecomparison',
    templateUrl: './quotecomparison.component.html',
    styleUrls: ['./quotecomparison.component.scss']
})
export class QuoteComparisonComponent implements OnInit {
    submitted = false;
    loading: boolean = false;
    showAlert: boolean = false;
    alertProperties = { type: '', message: '' };
    modalState: boolean = false;
    modalTemplate: any = {};
    modalSize: any = {};
    isError: boolean = false;
    loginErrorMessage: string = '';
    quotationDetail: any;
    insuranceDateTo: Date | null = null;
    selectedProvider: any = null;
    readonly sstRate = 0.08;
    roadtaxYes: boolean | null = null;
    roadtaxType = {
        digital: false,
        physical: false
    };
    roadtaxOption: 'digital' | 'physical' | null = null;

    constructor(private route: ActivatedRoute, private router: Router, private quotationService: ApolloQuotationService,
        private http: HttpClient
    ) { }
    @ViewChild('saveTemplateHeader', { static: true }) saveTemplateHeader!: TemplateRef<object>;
    @ViewChild('saveTemplateBody', { static: true }) saveTemplateBody!: TemplateRef<object>;
    @ViewChild('saveTemplateFooter', { static: true }) saveTemplateFooter!: TemplateRef<object>;

    ngOnInit() {
        const guid: string | null = this.route.snapshot.queryParamMap.get('guid');
        if (guid) {
            this.getQuotationByGuid(guid);
        } else {
            console.error('No GUID provided in query parameters');
        }
    }

    getQuotationByGuid(guid: string) {
        this.loading = true;
        this.quotationService.getQuotationByGUID(guid).subscribe(
            (response: ResponseDTO<QuotationPoco[]>) => {
                if (response?.data) {
                    this.quotationDetail = response.data;
                    const currentExpDateStr = this.quotationDetail?.customerVehicle?.currentInsuranceExpirationDate;
                    if (currentExpDateStr) {
                        const currentExpDate = new Date(currentExpDateStr);
                        const computedDate = new Date(currentExpDate);
                        computedDate.setFullYear(computedDate.getFullYear() + 1);
                        computedDate.setDate(computedDate.getDate() - 1);
                        this.insuranceDateTo = computedDate;
                    }
                } else {
                    this.handleLoginError(response.error || 'Failed to get quotation list');
                }
                this.loading = false;
            },
            (error) => {
                this.handleLoginError('Failed to get quotation list');
                this.loading = false;
            }
        );
    }

    private round(value: number, decimals = 2): number {
        return Number(value.toFixed(decimals));
    }

    computePremiumDue(basic: number): number {
        const ncdPct = this.quotationDetail.customerVehicle.ncd;
        return this.round(basic * (1 - ncdPct / 100));
    }

    computeSst(basic: number): number {
        const due = this.computePremiumDue(basic);
        const extras = this.additionalBenefits.filter(item => !item.name.startsWith('Roadtax') && item.name !== 'E-Hailing Charge').reduce((sum, item) => sum + item.price, 0);
        return this.round((due + extras) * this.sstRate);
    }

    computePremiumPayable(basic: number): number {
        const due = this.computePremiumDue(basic);
        const sst = this.computeSst(basic);
        return this.round(due + sst + this.quotationDetail.serviceCharge);
    }

    computeTotal(basic: number): number {
        if (this.quotationDetail?.insuranceType !== 'Lump Sum') {
            return this.computePremiumPayable(basic);
        } else {
            const due = this.computePremiumDue(basic);
            return this.round(due);
        }
    }

    computeGrossPremium(basic: number): number {
        const ncdPct = this.quotationDetail.customerVehicle.ncd / 100;
        const afterNcd = this.round(basic * (1 - ncdPct));
        return this.round(afterNcd);
    }

    computeGrandTotal(basic: number): number {
        if (this.quotationDetail?.insuranceType !== 'Lump Sum') {
            const afterNcd = this.computeGrossPremium(basic);
            const sst = this.computeSst(basic);
            const extras = this.computeAdditionalTotal();
            return this.round(afterNcd + sst + extras + this.quotationDetail.serviceCharge);
        } else {
            const afterNcd = this.computeGrossPremium(basic);
            return this.round(afterNcd);
        }
    }

    get additionalBenefits(): { name: string; price: number }[] {
        if (!this.selectedProvider) return [];

        const list: { name: string; price: number }[] = [];
        const ehailing = Number(this.quotationDetail.ehailingCharge) || 0;

        if (this.roadtaxYes) {
            list.push({ name: 'Roadtax Price', price: Number(this.quotationDetail.roadtaxPrice) });
            if (this.roadtaxOption === 'physical') {
                list.push({ name: 'Roadtax Physical Charge', price: Number(this.quotationDetail.roadtaxDeliveryCharge) });
                list.push({ name: 'E-Hailing Charge', price: ehailing });
            } else if (this.roadtaxOption === 'digital') {
                list.push({ name: 'Roadtax Digital Charge', price: Number(this.quotationDetail.roadtaxDigitalCharge) });
            }
        }

        list.push(
            ...this.selectedProvider.quotationAddOnsPoco
                .filter((a: any) => a.isSelected)
                .map((a: any) => ({
                    name: a.refQuotationAddOnsType.name,
                    price: a.price
                }))
        );

        return list;
    }

    get selectedAddOns(): any[] {
        return this.selectedProvider ? this.selectedProvider.quotationAddOnsPoco.filter((a: any) => a.isSelected) : [];
    }

    computeAdditionalTotal(): number {
        const llop = Number(this.quotationDetail.llop) || 0;
        const total = this.additionalBenefits.reduce((sum, item) => sum + item.price, llop);
        return this.round(total);
    }

    isSpecialInputAddon(addon: any): boolean {
        const name = addon?.refQuotationAddOnsType?.name || '';
        return name.includes('Windscreen Coverage');
    }

    isSpecialCheckAddon(addon: any): boolean {
        const name = addon?.refQuotationAddOnsType?.name || '';
        return name.includes('Extreme Weather Coverage');
    }

    getMinSumInsured(addon: any): number {
        const name = addon?.refQuotationAddOnsType?.name || '';
        if (name.includes('Windscreen Coverage')) return 400;
        return 0;
    }

    onSumInsuredChanged(addon: any, inputValue: number): void {
        const insuranceProviderAddOnsId = addon.refQuotationAddOnsType.insuranceProviderAddOnsId;
        addon.sumInsuredInput = inputValue;
        const isCheck = this.isSpecialCheckAddon(addon);

        if (isCheck || inputValue >= this.getMinSumInsured(addon)) {
            const addOnTemplate = [
                { id: 29, code: "D034", premium: 0, sumInsured: 0 },
                { id: 9, code: "P057", premium: 0, sumInsured: 0 },
                // { id: 8, code: "D003", premium: 0, sumInsured: 0 },
                { id: 7, code: "B089", premium: 0, sumInsured: 0 }
            ];

            const updatedAddOns = addOnTemplate.map(addOn => {
                if (addOn.id === insuranceProviderAddOnsId) {
                    return {
                        ...addOn,
                        sumInsured: inputValue,
                        isValid: true
                    };
                }
                return { ...addOn };
            });
            const motorCalculatePremiumBody = {
                quotationNo: "",
                productId: 3,
                productCode: "MI",
                isRenewal: false,
                sumInsured: 0,
                agentCode: "",
                agentType: 0,
                isStaff: false,
                customer: {
                    identityType: "1",
                    identityNumber: this.quotationDetail?.customer?.nric || '',
                    ethnicityId: null
                },
                vehicle: {
                    nvic: "",
                    registrationNo: this.quotationDetail?.customerVehicle?.plateNumber,
                    useAgreedValue: false,
                    postcode: this.quotationDetail?.customer?.postcode,
                    chassisNo: "",
                    engineNo: ""
                },
                addOn: updatedAddOns,
                hasPersonalAccident: false,
                personalAccident: {
                    productCode: "",
                    planClass: 1
                },
                language: "en",
                isPartner: false,
                countryCode: "MYS",
                isMAEM2U: false
            };
            const API_URL = environment.API_URL;
            this.http.post<any>(`${API_URL}/EtiqaProxy/CalculatePremium`, motorCalculatePremiumBody).subscribe(
                (res: any) => {
                    const addOnPremiums = res?.data?.addOn || [];
                    const matched = addOnPremiums.find((a: any) => a.addOnId === insuranceProviderAddOnsId);
                    if (matched && matched.grossPremium > 0) {
                        addon.price = matched.grossPremium;
                    }
                    this.selectedProvider.quotationAddOnsPoco.forEach((a: any) => {
                        const id = a.refQuotationAddOnsType.insuranceProviderAddOnsId;
                        if (a === addon || this.isSpecialInputAddon(a)) return;

                        const fixed = addOnPremiums.find((api: any) => api.addOnId === id);
                        if (fixed && fixed.grossPremium > 0) {
                            a.price = fixed.grossPremium;
                        }
                    });
                },
                (error: any) => {
                    console.error('Premium Calculation Error:', error);
                }
            );
        }
    }

    selectPlan(provider: any) {
        this.selectedProvider = {
            ...provider,
            quotationAddOnsPoco: provider.quotationAddOnsPoco.map((a: any) => ({
                ...a,
                isSelected: null
            }))
        };
    }

    selectRoadtax(flag: boolean) {
        this.roadtaxYes = flag;
        if (!flag) {
            this.roadtaxType = { digital: false, physical: false };
        }
    }

    get totalPrice(): number {
        if (!this.roadtaxYes) return 0;
        const base = this.quotationDetail.roadtaxPrice;
        const extra = this.roadtaxOption === 'digital'
            ? this.quotationDetail.roadtaxDigitalCharge
            : this.quotationDetail.roadtaxDeliveryCharge;
        return base + extra;
    }

    toggleAddon(addon: any, value: boolean) {
        addon.isSelected = value;
        if (this.isSpecialCheckAddon(addon)) {
            addon.sumInsuredInput = 0;
            if (value) {
                this.onSumInsuredChanged(addon, 0);
            } else {
                addon.price = 0;
            }
            return;
        }
    }

    cancelSelection() {
        this.selectedProvider = null;
    }

    onSave() {
        this.submitted = true;
        this.modalSize = { width: '500px', height: '250px' };
        this.modalState = true;
        this.modalTemplate = { header: this.saveTemplateHeader, body: this.saveTemplateBody, footer: this.saveTemplateFooter };
    }

    onSubmit() {
        this.submitted = true;
        this.loading = true;
        const toDate = (d?: string | Date) => d ? new Date(d).toISOString() : undefined;
        const { id, transactionId } = this.quotationDetail.payment || {};
        const basic = this.selectedProvider.basicPremium;
        const totalPremium = this.computeGrandTotal(basic);
        const quotationInsuranceProviderListInput = this.quotationDetail.quotationInsuranceProviderList.map((prov: any) => ({
            id: prov.id,
            insuranceProviderId: prov.insuranceProvider.id,
            quotationId: this.quotationDetail?.id,
            basicPremium: this.selectedProvider.basicPremium,
            isSelected: prov.insuranceProvider.id === this.selectedProvider.insuranceProvider.id,
            quotationAddOnsPoco: this.selectedProvider.quotationAddOnsPoco.map((a: any) => ({
                id: a.id,
                quotationInsuranceProviderId: prov.id,
                isSelected: a.isSelected,
                price: a.price
            }))
        }));

        const paymentInput: Partial<PaymentPoco> = {
            quotationId: this.quotationDetail?.id,
            totalPaid: totalPremium,
            paymentMethod: "Online Banking",
            ...(id != null && { id }),
            ...(transactionId != null && { transactionId }),
        };
        const quotationInput: Partial<QuotationPoco> = {
            id: this.quotationDetail?.id,
            insuranceType: this.quotationDetail?.insuranceType,
            roadtaxPrice: this.quotationDetail?.insuranceType === 'Lump Sum' ? 0 : (Number(this.quotationDetail?.roadtaxPrice) || 0),
            isRoadtax: this.roadtaxYes || false,
            roadtaxDigitalCharge: this.quotationDetail?.insuranceType === 'Lump Sum' ? 0 : (Number(this.quotationDetail?.roadtaxDigitalCharge) || 0),
            roadtaxDeliveryCharge: this.quotationDetail?.insuranceType === 'Lump Sum' ? 0 : (Number(this.quotationDetail?.roadtaxDeliveryCharge) || 0),
            isPhysicalRoadtax: (this.roadtaxOption === 'physical') || false,
            serviceCharge: this.quotationDetail?.insuranceType === 'Lump Sum' ? 0 : (Number(this.quotationDetail?.serviceCharge) || 0),
            remarks: this.quotationDetail?.remarks,
            llop: this.quotationDetail?.insuranceType === 'Lump Sum' ? 0 : (Number(this.quotationDetail?.llop) || 0),
            totalPremium: totalPremium,
            ehailingCharge: this.quotationDetail?.ehailingCharge,
            guid: this.quotationDetail?.guid,
            customerId: this.quotationDetail?.customer?.id,
            customerVehicleId: this.quotationDetail?.customerVehicle?.id,
            trackingNumber: this.quotationDetail?.trackingNumber,

            picUser: {
                id: this.quotationDetail?.picUser?.id
            } as any,

            quotationInsuranceProviderList: quotationInsuranceProviderListInput,
            customer: {
                id: this.quotationDetail?.customer?.id,
                name: this.quotationDetail?.customer?.name,
                nric: this.quotationDetail?.customer?.nric,
                phoneNumber: this.quotationDetail?.customer?.phoneNumber,
                email: this.quotationDetail?.customer?.email,
                firstAddress: this.quotationDetail?.customer?.firstAddress,
                secondAddress: this.quotationDetail?.customer?.secondAddress,
                city: this.quotationDetail?.customer?.city,
                state: this.quotationDetail?.customer?.state,
                postcode: this.quotationDetail?.customer?.postcode,
                country: this.quotationDetail?.customer?.country,
            } as any,

            customerVehicle: {
                id: this.quotationDetail?.customerVehicle?.id,
                brand: this.quotationDetail?.customerVehicle?.brand,
                model: this.quotationDetail?.customerVehicle?.model,
                plateNumber: this.quotationDetail?.customerVehicle?.plateNumber,
                manufactureYear: this.quotationDetail?.customerVehicle?.manufactureYear,
                engineCapacity: this.quotationDetail?.customerVehicle?.engineCapacity,
                ncd: parseFloat(this.quotationDetail?.customerVehicle?.ncd),
                isEhailing: this.quotationDetail?.customerVehicle?.isEhailing,
                vehicleType: this.quotationDetail?.customerVehicle?.vehicleType,
                currentInsuranceExpirationDate: toDate(this.quotationDetail?.customerVehicle?.currentInsuranceExpirationDate),
                evpExpirationDate: toDate(this.quotationDetail?.customerVehicle?.evpExpirationDate),
                psvExpirationDate: toDate(this.quotationDetail?.customerVehicle?.psvExpirationDate),
                puspakomExpirationDate: toDate(this.quotationDetail?.customerVehicle?.puspakomExpirationDate)
            } as any,
        };
        const custInput: Partial<CustomerPoco> = {
            id: this.quotationDetail?.customer?.id,
            name: this.quotationDetail?.customer?.name,
            nric: this.quotationDetail?.customer?.nric,
            phoneNumber: this.quotationDetail?.customer?.phoneNumber,
            email: this.quotationDetail?.customer?.email,
            firstAddress: this.quotationDetail?.customer?.firstAddress,
            secondAddress: this.quotationDetail?.customer?.secondAddress,
            city: this.quotationDetail?.customer?.city,
            state: this.quotationDetail?.customer?.state,
            postcode: this.quotationDetail?.customer?.postcode,
            country: this.quotationDetail?.customer?.country,
        };
        const custVehicleInput: Partial<CustomerVehiclePoco> = {
            id: this.quotationDetail?.customerVehicle?.id,
            brand: this.quotationDetail?.customerVehicle?.brand,
            model: this.quotationDetail?.customerVehicle?.model,
            plateNumber: this.quotationDetail?.customerVehicle?.plateNumber,
            manufactureYear: this.quotationDetail?.customerVehicle?.manufactureYear,
            engineCapacity: this.quotationDetail?.customerVehicle?.engineCapacity,
            ncd: parseFloat(this.quotationDetail?.customerVehicle?.ncd),
            isEhailing: this.quotationDetail?.customerVehicle?.isEhailing,
            vehicleType: this.quotationDetail?.customerVehicle?.vehicleType,
            currentInsuranceExpirationDate: toDate(this.quotationDetail?.customerVehicle?.currentInsuranceExpirationDate),
            evpExpirationDate: toDate(this.quotationDetail?.customerVehicle?.evpExpirationDate),
            psvExpirationDate: toDate(this.quotationDetail?.customerVehicle?.psvExpirationDate),
            puspakomExpirationDate: toDate(this.quotationDetail?.customerVehicle?.puspakomExpirationDate)
        };
        this.quotationService.createPayment(paymentInput, quotationInput, custInput, custVehicleInput).subscribe(
            (response) => {
                this.loading = false;
                if (response && response.data && response.data.url) {
                    window.location.href = response.data.url;
                } else {
                    this.displayAlert("danger", "Failed to generate Payment URL");
                }
            },
            (error) => {
                this.loading = false;
                this.displayAlert("danger", "Failed to create Payment");
            }
        );
        this.modalState = false;
    }

    onCloseModal() {
        this.modalState = false;
    }

    displayAlert(type: string, message: string) {
        this.showAlert = true;
        this.alertProperties = { type: type, message: message };
        window.setTimeout(() => {
            this.showAlert = false;
        }, 5000);
    }

    private handleLoginError(errorMessage: string) {
        this.isError = true;
        this.loginErrorMessage = errorMessage;
        this.displayAlert('danger', errorMessage);
    }
}